import { useEffect, useState, type ChangeEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Database, SearchX } from "lucide-react";
import { api } from "@/api/client";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";
import { NativeSelect } from "@/components/ui/native-select";
import { ScoreBar } from "@/components/ui/score-bar";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type {
  SentimentExplanation,
  SentimentExplanationCompetitorComparison,
  SentimentExplanationContributor,
  SentimentExplanationContributorSection,
  SentimentExplanationEvidence,
  SentimentExplanationFact,
  SentimentExplanationGapDimension,
  SentimentExplanationSideBySideEvidence,
} from "../../../types";

export interface SentimentExplanationCompetitorOption {
  brandId: number;
  brandName: string;
}

interface SentimentExplanationPanelProps {
  brandId: number;
  brandName: string;
  rangeKey: string;
  competitorOptions: SentimentExplanationCompetitorOption[];
  /** Plural prompt lens (e.g. the tracking plan's prompt set on Brand Health). */
  promptIds?: number[];
}

interface ExplanationContentProps {
  explanation: SentimentExplanation;
  selectedCompetitorId: number | null;
  competitorOptions: SentimentExplanationCompetitorOption[];
  onCompetitorChange: (event: ChangeEvent<HTMLSelectElement>) => void;
}

interface SummaryStatProps {
  label: string;
  value: ReactNode;
  detail?: string;
}

interface EvidenceListProps {
  title: string;
  items: SentimentExplanationEvidence[];
  emptyMessage: string;
}

interface EvidenceCardProps {
  evidence: SentimentExplanationEvidence;
}

interface ContributorSectionProps {
  title: string;
  description: string;
  section: SentimentExplanationContributorSection;
  emptyLabel: string;
}

interface ContributorListProps {
  title: string;
  contributors: SentimentExplanationContributor[];
  emptyLabel: string;
}

interface ContributorRowProps {
  contributor: SentimentExplanationContributor;
}

interface FactGridProps {
  facts: SentimentExplanationFact[];
}

interface CaveatListProps {
  title: string;
  caveats: string[];
}

interface CompetitorSectionProps {
  selectedCompetitorId: number | null;
  competitorOptions: SentimentExplanationCompetitorOption[];
  comparison: SentimentExplanationCompetitorComparison | null;
  onCompetitorChange: (event: ChangeEvent<HTMLSelectElement>) => void;
}

interface CompetitorComparisonViewProps {
  comparison: SentimentExplanationCompetitorComparison | null;
}

interface GapDimensionTableProps {
  title: string;
  rows: SentimentExplanationGapDimension[];
}

interface SideBySideEvidenceListProps {
  rows: SentimentExplanationSideBySideEvidence[];
}

interface SideBySideEvidenceCardProps {
  row: SentimentExplanationSideBySideEvidence;
}

interface SideSnippetProps {
  label: string;
  evidence: SentimentExplanationEvidence | null;
}

const EMPTY_CONTRIBUTOR_SECTION: SentimentExplanationContributorSection = {
  all: [],
  strongest: [],
  weakest: [],
};

/**
 * Keeps array reads defensive when API sections are absent or malformed.
 *
 * @param value Candidate array value.
 * @return Safe array value.
 */
function safeArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

/**
 * Returns a safe contributor section for optional API response data.
 *
 * @param section Candidate contributor section.
 * @return Contributor section with safe arrays.
 */
function safeContributorSection(section: SentimentExplanationContributorSection | null | undefined): SentimentExplanationContributorSection {
  if (!section) return EMPTY_CONTRIBUTOR_SECTION;

  return {
    all: safeArray(section.all),
    strongest: safeArray(section.strongest),
    weakest: safeArray(section.weakest),
  };
}

/**
 * Rounds a score for display without changing backend calculations.
 *
 * @param value Candidate score value.
 * @return Rounded score or null.
 */
function roundScore(value: number | null | undefined): number | null {
  if (typeof value !== "number" || Number.isNaN(value)) return null;
  return Number(value.toFixed(1));
}

/**
 * Formats a nullable score for display.
 *
 * @param value Candidate score value.
 * @return Display-ready score.
 */
function formatScore(value: number | null | undefined): string {
  if (typeof value !== "number" || Number.isNaN(value)) return "--";
  return value.toFixed(1);
}

/**
 * Formats a nullable gap with an explicit sign.
 *
 * @param value Candidate gap value.
 * @return Display-ready signed gap.
 */
function formatSignedScore(value: number | null | undefined): string {
  if (typeof value !== "number" || Number.isNaN(value)) return "--";
  return `${value > 0 ? "+" : ""}${value.toFixed(1)}`;
}

/**
 * Formats a count with singular or plural wording.
 *
 * @param count Count value.
 * @param singular Singular noun.
 * @param plural Optional plural noun.
 * @return Display-ready count label.
 */
function formatCount(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * Parses a SQLite or ISO datetime as UTC.
 *
 * @param value Datetime string.
 * @return Date object.
 */
function parseUtcDate(value: string): Date {
  return new Date(value.includes("T") ? value : `${value.replace(" ", "T")}Z`);
}

/**
 * Formats a stored UTC datetime for compact display.
 *
 * @param value Datetime string.
 * @return Display-ready date.
 */
function formatUtcDateTime(value: string): string {
  const date = parseUtcDate(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  });
}

/**
 * Formats a stored UTC date range for compact display.
 *
 * @param from Start datetime.
 * @param to End datetime.
 * @return Display-ready date range.
 */
function formatUtcRange(from: string, to: string): string {
  return `${formatUtcDateTime(from)} – ${formatUtcDateTime(to)}`;
}

/**
 * Formats a stored evidence source field for UI display.
 *
 * @param sourceField Source field from the API.
 * @return Display label.
 */
function formatSourceField(sourceField: SentimentExplanationEvidence["source_field"]): string {
  const labels: Record<SentimentExplanationEvidence["source_field"], string> = {
    "results.reasoning": "Result reasoning",
    "comparison_mentions.context": "Mention context",
    "comparisons.summary": "Comparison summary",
    "comparisons.raw_response": "Raw response",
  };

  return labels[sourceField] ?? sourceField;
}

/**
 * Formats the backend score calculation for humans.
 *
 * @param calculation Calculation key from the API.
 * @return Display label.
 */
function formatCalculation(calculation: SentimentExplanation["score"]["provenance"]["calculation"]): string {
  if (calculation === "latest_bucket_average") return "latest dashboard bucket average";
  return "selected window average";
}

/**
 * Picks the evidence badge variant from its sentiment band.
 *
 * @param evidence Evidence item.
 * @return Badge variant.
 */
function getEvidenceBadgeVariant(evidence: SentimentExplanationEvidence): BadgeProps["variant"] {
  if (evidence.sentiment_band === "positive") return "success";
  if (evidence.sentiment_band === "negative") return "error";
  return "muted";
}

/**
 * Picks a badge variant for a signed sentiment gap.
 *
 * @param gap Sentiment gap.
 * @return Badge variant.
 */
function getGapBadgeVariant(gap: number | null): BadgeProps["variant"] {
  if (gap === null) return "muted";
  if (gap > 0) return "success";
  if (gap < 0) return "error";
  return "muted";
}

/**
 * Formats an API fact value without inventing interpretation.
 *
 * @param value Fact value.
 * @return Display-ready fact value.
 */
function formatFactValue(value: SentimentExplanationFact["value"]): string {
  if (typeof value === "number") return Number.isInteger(value) ? String(value) : value.toFixed(1);
  if (typeof value === "string" && value.trim()) return value;
  return "--";
}

/**
 * Builds a stable evidence key for React lists.
 *
 * @param evidence Evidence item.
 * @return Stable evidence key.
 */
function getEvidenceKey(evidence: SentimentExplanationEvidence): string {
  return [
    evidence.comparison_id,
    evidence.prompt_id,
    evidence.model,
    evidence.recorded_at,
    evidence.source_field,
    evidence.sentiment,
  ].join(":");
}

/**
 * Builds a stable side-by-side evidence key for React lists.
 *
 * @param row Side-by-side evidence row.
 * @return Stable evidence key.
 */
function getSideBySideKey(row: SentimentExplanationSideBySideEvidence): string {
  return [row.comparison_id, row.prompt_id, row.model, row.recorded_at].join(":");
}

/**
 * Returns a selected competitor only when it is still available.
 *
 * @param selectedCompetitorId Selected competitor ID.
 * @param competitorOptions Available competitor options.
 * @param brandId Subject brand ID.
 * @return Valid competitor ID or null.
 */
function getValidCompetitorId(
  selectedCompetitorId: number | null,
  competitorOptions: SentimentExplanationCompetitorOption[],
  brandId: number
): number | null {
  if (selectedCompetitorId === null || selectedCompetitorId === brandId) return null;
  return competitorOptions.some(option => option.brandId === selectedCompetitorId) ? selectedCompetitorId : null;
}

/**
 * Renders an option for a tracked competitor.
 *
 * @param option Competitor option.
 * @return Select option markup.
 */
function renderCompetitorOption(option: SentimentExplanationCompetitorOption): ReactNode {
  return (
    <option key={option.brandId} value={option.brandId}>
      {option.brandName}
    </option>
  );
}

/**
 * Renders an evidence card in a list.
 *
 * @param evidence Evidence item.
 * @return Evidence card markup.
 */
function renderEvidenceCard(evidence: SentimentExplanationEvidence): ReactNode {
  return <EvidenceCard key={getEvidenceKey(evidence)} evidence={evidence} />;
}

/**
 * Renders a contributor row in a list.
 *
 * @param contributor Contributor slice.
 * @return Contributor row markup.
 */
function renderContributorRow(contributor: SentimentExplanationContributor): ReactNode {
  return <ContributorRow key={contributor.key} contributor={contributor} />;
}

/**
 * Renders a measured fact card.
 *
 * @param fact Measured fact.
 * @return Fact card markup.
 */
function renderFact(fact: SentimentExplanationFact): ReactNode {
  return (
    <div key={fact.label} className="rounded-lg border border-slate-100 bg-slate-50/50 p-3">
      <dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400">{fact.label}</dt>
      <dd className="mt-1">
        <span className="text-sm font-semibold text-brand-950 tabular-nums">{formatFactValue(fact.value)}</span>
        {fact.detail && <span className="mt-1 block text-xs leading-5 text-slate-500">{fact.detail}</span>}
      </dd>
    </div>
  );
}

/**
 * Renders a caveat list item.
 *
 * @param caveat Caveat text.
 * @param index Caveat index.
 * @return Caveat markup.
 */
function renderCaveat(caveat: string, index: number): ReactNode {
  return (
    <li key={`${index}-${caveat}`} className="text-xs leading-5 text-slate-500">
      {caveat}
    </li>
  );
}

/**
 * Renders a gap dimension table row.
 *
 * @param row Gap dimension row.
 * @return Table row markup.
 */
function renderGapDimensionRow(row: SentimentExplanationGapDimension): ReactNode {
  return (
    <TableRow key={row.key}>
      <TableCell className="min-w-[180px] text-sm font-medium text-brand-950">{row.label}</TableCell>
      <TableCell className="text-right tabular-nums text-slate-700">{formatScore(row.subject_avg_sentiment)}</TableCell>
      <TableCell className="text-right tabular-nums text-slate-700">{formatScore(row.competitor_avg_sentiment)}</TableCell>
      <TableCell className="text-right">
        <Badge variant={getGapBadgeVariant(row.sentiment_gap)}>{formatSignedScore(row.sentiment_gap)}</Badge>
      </TableCell>
      <TableCell className="text-right tabular-nums text-slate-500">{row.sample_size}</TableCell>
    </TableRow>
  );
}

/**
 * Renders a side-by-side evidence card in a list.
 *
 * @param row Side-by-side evidence row.
 * @return Evidence card markup.
 */
function renderSideBySideCard(row: SentimentExplanationSideBySideEvidence): ReactNode {
  return <SideBySideEvidenceCard key={getSideBySideKey(row)} row={row} />;
}

/**
 * Builds matched competitor summary copy using observed language.
 *
 * @param comparison Competitor comparison response.
 * @return Summary sentence.
 */
function getCompetitorSummary(comparison: SentimentExplanationCompetitorComparison): string {
  if (comparison.status === "matched") {
    return `Matched on ${formatCount(comparison.matched_sample_size, "stored sentiment row")} across ${formatCount(comparison.comparison_count, "comparison")}. ${comparison.subject.brand_name} averaged ${formatScore(comparison.subject.avg_sentiment)} and ${comparison.competitor.brand_name} averaged ${formatScore(comparison.competitor.avg_sentiment)}; the observed sentiment gap is ${formatSignedScore(comparison.sentiment_gap)}.`;
  }

  if (comparison.status === "insufficient_overlap") {
    return "Stored sentiment exists for both brands in this range, but Prism did not find same-comparison overlap to compute a matched gap. The averages shown are range averages, not matched head-to-head results.";
  }

  return "No stored competitor sentiment metric rows matched this range, so a competitor gap is unavailable.";
}

/**
 * Formats a competitor comparison status for display.
 *
 * @param status Competitor comparison status.
 * @return Display label.
 */
function formatCompetitorStatus(status: SentimentExplanationCompetitorComparison["status"]): string {
  if (status === "matched") return "Matched comparison";
  if (status === "insufficient_overlap") return "Insufficient overlap";
  return "No competitor data";
}

/**
 * Formats the sample-size detail for competitor comparison averages.
 *
 * @param comparison Competitor comparison response.
 * @param sampleSize Sample size value.
 * @return Display-ready sample detail.
 */
function formatCompetitorSampleDetail(comparison: SentimentExplanationCompetitorComparison, sampleSize: number): string {
  return formatCount(sampleSize, comparison.status === "matched" ? "matched row" : "range row");
}

/**
 * Renders one score summary stat.
 *
 * @param props Summary stat props.
 * @return Summary stat markup.
 */
function SummaryStat({ label, value, detail }: SummaryStatProps) {
  return (
    <div className="rounded-lg border border-slate-100 bg-white p-4">
      <dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400">{label}</dt>
      <dd className="mt-1">
        <span className="text-base font-semibold text-brand-950 tabular-nums">{value}</span>
        {detail && <span className="mt-1 block text-xs leading-5 text-slate-500">{detail}</span>}
      </dd>
    </div>
  );
}

/**
 * Renders the score summary and provenance for the explanation.
 *
 * @param props Explanation content props.
 * @return Score summary markup.
 */
function ScoreSummary({ explanation }: { explanation: SentimentExplanation }) {
  const score = explanation.score;
  const scoreWindow = explanation.score_window;
  const models = safeArray(explanation.models);
  const prompts = safeArray(explanation.prompts);

  return (
    <section className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
      <div className="rounded-lg border border-brand-100 bg-brand-50/50 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-brand-600">Score summary</p>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-4xl font-bold leading-none text-brand-950 tabular-nums">{formatScore(score.value)}</span>
              <span className="text-sm text-slate-500">sentiment</span>
            </div>
          </div>
          <Badge variant="muted">No fresh model call</Badge>
        </div>
        <div className="mt-5">
          <ScoreBar score={roundScore(score.value)} size="default" />
        </div>
        <p className="mt-4 text-sm leading-6 text-slate-600">{score.formula}</p>
        <p className="mt-2 text-xs leading-5 text-slate-500">
          Source: {score.provenance.source_table}.{score.provenance.source_metric}; calculation: {formatCalculation(score.provenance.calculation)}. Prism renders stored response evidence only.
        </p>
      </div>

      <dl className="grid gap-3 sm:grid-cols-2">
        <SummaryStat
          label="Score sample"
          value={score.sample_size}
          detail={`Rows in ${scoreWindow.label ? `bucket ${scoreWindow.label}` : "the score window"}`}
        />
        <SummaryStat
          label="Selected range"
          value={explanation.range.label}
          detail={formatUtcRange(explanation.range.from, explanation.range.to)}
        />
        <SummaryStat
          label="Models"
          value={models.length}
          detail={models.length > 0 ? models.slice(0, 2).join(", ") : "No models represented"}
        />
        <SummaryStat
          label="Prompts"
          value={prompts.length}
          detail={`${score.range_sample_size} stored sentiment row${score.range_sample_size === 1 ? "" : "s"} in range`}
        />
      </dl>
    </section>
  );
}

/**
 * Renders positive and negative stored-answer evidence.
 *
 * @param props Evidence list props.
 * @return Evidence list markup.
 */
function EvidenceList({ title, items, emptyMessage }: EvidenceListProps) {
  return (
    <div className="rounded-lg border border-slate-100 bg-white p-4">
      <h5 className="text-sm font-semibold text-brand-950">{title}</h5>
      <div className="mt-3 space-y-3">
        {items.length > 0 ? items.map(renderEvidenceCard) : (
          <p className="rounded-md bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">{emptyMessage}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Renders a single exact stored evidence snippet.
 *
 * @param props Evidence card props.
 * @return Evidence card markup.
 */
function EvidenceCard({ evidence }: EvidenceCardProps) {
  return (
    <article className="rounded-md border border-slate-100 bg-slate-50/60 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={getEvidenceBadgeVariant(evidence)}>sent {formatScore(evidence.sentiment)}</Badge>
        <Badge variant="muted">{formatSourceField(evidence.source_field)}</Badge>
        {evidence.is_truncated && <Badge variant="warning">Truncated</Badge>}
      </div>
      <blockquote className="mt-3 text-sm leading-6 text-slate-700">
        “{evidence.snippet}{evidence.is_truncated ? "…" : ""}”
      </blockquote>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
        <span>
          {evidence.prompt_name} · {evidence.model} · {formatUtcDateTime(evidence.recorded_at)}
        </span>
        <Link className="font-semibold text-brand-600 hover:text-brand-500 hover:underline" to={`/app/compare/results/${evidence.comparison_id}`}>
          View original response
        </Link>
      </div>
    </article>
  );
}

/**
 * Renders the stored-answer evidence section.
 *
 * @param props Explanation content props.
 * @return Evidence section markup.
 */
function EvidenceSection({ explanation }: { explanation: SentimentExplanation }) {
  return (
    <section className="space-y-3">
      <div>
        <h4 className="text-sm font-semibold text-brand-950">Evidence from stored LLM answers</h4>
        <p className="mt-1 text-xs leading-5 text-slate-500">Exact snippets are shown from stored response fields and are not rewritten summaries.</p>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <EvidenceList
          title="Positive snippets"
          items={safeArray(explanation.positive_evidence)}
          emptyMessage="No positive snippets found"
        />
        <EvidenceList
          title="Negative and caveat snippets"
          items={safeArray(explanation.negative_evidence)}
          emptyMessage="No negative snippets found"
        />
      </div>
    </section>
  );
}

/**
 * Renders contributor slices for prompts or models.
 *
 * @param props Contributor section props.
 * @return Contributor section markup.
 */
function ContributorSection({ title, description, section, emptyLabel }: ContributorSectionProps) {
  return (
    <div className="rounded-lg border border-slate-100 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h5 className="text-sm font-semibold text-brand-950">{title}</h5>
          <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
        </div>
        <Badge variant="muted">{section.all.length} observed</Badge>
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <ContributorList title="Highest sentiment slices" contributors={section.strongest} emptyLabel={emptyLabel} />
        <ContributorList title="Lowest sentiment slices" contributors={section.weakest} emptyLabel={emptyLabel} />
      </div>
    </div>
  );
}

/**
 * Renders a contributor list.
 *
 * @param props Contributor list props.
 * @return Contributor list markup.
 */
function ContributorList({ title, contributors, emptyLabel }: ContributorListProps) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400">{title}</p>
      <div className="mt-3 space-y-3">
        {contributors.length > 0 ? contributors.map(renderContributorRow) : (
          <p className="rounded-md bg-slate-50 px-3 py-4 text-center text-xs text-slate-500">No {emptyLabel} slices found</p>
        )}
      </div>
    </div>
  );
}

/**
 * Renders one contributor slice with its stored sentiment average.
 *
 * @param props Contributor row props.
 * @return Contributor row markup.
 */
function ContributorRow({ contributor }: ContributorRowProps) {
  return (
    <div className="space-y-2 rounded-md border border-slate-100 bg-slate-50/60 p-3">
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 flex-1 truncate text-sm font-medium text-brand-950" title={contributor.label}>{contributor.label}</p>
        <span className="shrink-0 text-xs text-slate-500 tabular-nums">{formatCount(contributor.sample_size, "row")}</span>
      </div>
      <ScoreBar score={roundScore(contributor.avg_sentiment)} size="sm" />
    </div>
  );
}

/**
 * Renders measured fact cards from the API response.
 *
 * @param props Fact grid props.
 * @return Fact grid markup.
 */
function FactGrid({ facts }: FactGridProps) {
  if (facts.length === 0) return null;

  return (
    <section className="space-y-3">
      <h4 className="text-sm font-semibold text-brand-950">Stored metric facts</h4>
      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{facts.map(renderFact)}</dl>
    </section>
  );
}

/**
 * Renders caveats supplied by the explanation backend.
 *
 * @param props Caveat list props.
 * @return Caveat list markup.
 */
function CaveatList({ title, caveats }: CaveatListProps) {
  if (caveats.length === 0) return null;

  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-4">
      <p className="text-sm font-semibold text-brand-950">{title}</p>
      <ul className="mt-2 list-disc space-y-1 pl-5">{caveats.map(renderCaveat)}</ul>
    </div>
  );
}

/**
 * Renders prompt and model contributor sections.
 *
 * @param props Explanation content props.
 * @return Contributors markup.
 */
function Contributors({ explanation }: { explanation: SentimentExplanation }) {
  const promptSection = safeContributorSection(explanation.contributors?.prompts);
  const modelSection = safeContributorSection(explanation.contributors?.models);

  return (
    <section className="space-y-3">
      <div>
        <h4 className="text-sm font-semibold text-brand-950">Key driver slices observed in stored responses</h4>
        <p className="mt-1 text-xs leading-5 text-slate-500">Prompt and model averages are measured from stored sentiment metrics in the selected range.</p>
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <ContributorSection
          title="Prompt slices"
          description="Prompt groups with the highest and lowest observed sentiment averages."
          section={promptSection}
          emptyLabel="prompt"
        />
        <ContributorSection
          title="Model slices"
          description="Model groups with the highest and lowest observed sentiment averages."
          section={modelSection}
          emptyLabel="model"
        />
      </div>
    </section>
  );
}

/**
 * Renders the competitor comparison selector and selected comparison layer.
 *
 * @param props Competitor section props.
 * @return Competitor section markup.
 */
function CompetitorSection({ selectedCompetitorId, competitorOptions, comparison, onCompetitorChange }: CompetitorSectionProps) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="grid gap-4 lg:grid-cols-[1fr_260px] lg:items-start">
        <div>
          <h4 className="text-sm font-semibold text-brand-950">Compare against a competitor</h4>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            Choose a tracked competitor to compare matched prompts and models. Competitor analysis is separate from the base score explanation.
          </p>
        </div>
        <div>
          <label className="sr-only" htmlFor="sentiment-competitor-select">Compare against a competitor</label>
          <NativeSelect
            id="sentiment-competitor-select"
            value={selectedCompetitorId ?? ""}
            onChange={onCompetitorChange}
            disabled={competitorOptions.length === 0}
          >
            <option value="">Compare against a competitor</option>
            {competitorOptions.map(renderCompetitorOption)}
          </NativeSelect>
        </div>
      </div>

      <div className="mt-4">
        {competitorOptions.length === 0 ? (
          <p className="rounded-md bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">No tracked competitors are available for this dashboard range.</p>
        ) : selectedCompetitorId === null ? (
          <p className="rounded-md bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">Select a competitor to add a matched comparison layer.</p>
        ) : (
          <CompetitorComparisonView comparison={comparison} />
        )}
      </div>
    </section>
  );
}

/**
 * Renders selected competitor comparison details.
 *
 * @param props Competitor comparison view props.
 * @return Competitor comparison markup.
 */
function CompetitorComparisonView({ comparison }: CompetitorComparisonViewProps) {
  if (!comparison) {
    return <p className="rounded-md bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">No competitor comparison was returned for the selected competitor.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Badge variant={comparison.status === "matched" ? "default" : "warning"}>{formatCompetitorStatus(comparison.status)}</Badge>
          <span className="text-xs text-slate-500">Match strategy: {comparison.match_strategy === "same_comparison" ? "same stored comparison" : "none"}</span>
        </div>
        <p className="mt-3 text-sm leading-6 text-slate-700">{getCompetitorSummary(comparison)}</p>
        <dl className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryStat label={comparison.subject.brand_name} value={formatScore(comparison.subject.avg_sentiment)} detail={formatCompetitorSampleDetail(comparison, comparison.subject.sample_size)} />
          <SummaryStat label={comparison.competitor.brand_name} value={formatScore(comparison.competitor.avg_sentiment)} detail={formatCompetitorSampleDetail(comparison, comparison.competitor.sample_size)} />
          <SummaryStat label="Observed gap" value={<Badge variant={getGapBadgeVariant(comparison.sentiment_gap)}>{formatSignedScore(comparison.sentiment_gap)}</Badge>} detail="Subject minus competitor" />
          <SummaryStat label="Matched sample" value={comparison.matched_sample_size} detail={formatCount(comparison.comparison_count, "comparison")} />
        </dl>
      </div>

      <CaveatList title="Competitor caveats" caveats={safeArray(comparison.caveats)} />

      {comparison.status === "matched" && (
        <div className="grid gap-4 xl:grid-cols-2">
          <GapDimensionTable title="Matched prompt rows" rows={safeArray(comparison.by_prompt)} />
          <GapDimensionTable title="Matched model rows" rows={safeArray(comparison.by_model)} />
        </div>
      )}

      {comparison.status === "matched" && <SideBySideEvidenceList rows={safeArray(comparison.side_by_side_evidence)} />}
    </div>
  );
}

/**
 * Renders matched competitor gap dimensions.
 *
 * @param props Gap dimension table props.
 * @return Gap dimension table markup.
 */
function GapDimensionTable({ title, rows }: GapDimensionTableProps) {
  return (
    <div className="rounded-lg border border-slate-100 bg-white p-4">
      <h5 className="text-sm font-semibold text-brand-950">{title}</h5>
      {rows.length === 0 ? (
        <p className="mt-3 rounded-md bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">No matched rows available.</p>
      ) : (
        <div className="mt-3">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Slice</TableHead>
                <TableHead className="text-right">Subject</TableHead>
                <TableHead className="text-right">Competitor</TableHead>
                <TableHead className="text-right">Gap</TableHead>
                <TableHead className="text-right">Rows</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>{rows.map(renderGapDimensionRow)}</TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

/**
 * Renders side-by-side evidence for matched competitor rows.
 *
 * @param props Side-by-side evidence list props.
 * @return Side-by-side evidence markup.
 */
function SideBySideEvidenceList({ rows }: SideBySideEvidenceListProps) {
  return (
    <div className="rounded-lg border border-slate-100 bg-white p-4">
      <h5 className="text-sm font-semibold text-brand-950">Side-by-side evidence</h5>
      <p className="mt-1 text-xs leading-5 text-slate-500">Stored snippets from matched comparisons, shown without rewriting.</p>
      <div className="mt-3 space-y-3">
        {rows.length > 0 ? rows.map(renderSideBySideCard) : (
          <p className="rounded-md bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">No side-by-side snippets found.</p>
        )}
      </div>
    </div>
  );
}

/**
 * Renders one matched competitor side-by-side evidence row.
 *
 * @param props Side-by-side evidence card props.
 * @return Side-by-side evidence card markup.
 */
function SideBySideEvidenceCard({ row }: SideBySideEvidenceCardProps) {
  return (
    <article className="rounded-md border border-slate-100 bg-slate-50/60 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={getGapBadgeVariant(row.sentiment_gap)}>{formatSignedScore(row.sentiment_gap)} gap</Badge>
          <span className="text-xs text-slate-500">{row.prompt_name} · {row.model} · {formatUtcDateTime(row.recorded_at)}</span>
        </div>
        <Link className="text-xs font-semibold text-brand-600 hover:text-brand-500 hover:underline" to={`/app/compare/results/${row.comparison_id}`}>
          View original response
        </Link>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <SideSnippet label={`Subject · sent ${formatScore(row.subject_sentiment)}`} evidence={row.subject_evidence} />
        <SideSnippet label={`Competitor · sent ${formatScore(row.competitor_sentiment)}`} evidence={row.competitor_evidence} />
      </div>
    </article>
  );
}

/**
 * Renders one side-by-side snippet column.
 *
 * @param props Side snippet props.
 * @return Side snippet markup.
 */
function SideSnippet({ label, evidence }: SideSnippetProps) {
  return (
    <div className="rounded-md bg-white p-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400">{label}</p>
      {evidence ? (
        <blockquote className="mt-2 text-sm leading-6 text-slate-700">
          “{evidence.snippet}{evidence.is_truncated ? "…" : ""}”
        </blockquote>
      ) : (
        <p className="mt-2 text-sm text-slate-500">No stored snippet found.</p>
      )}
    </div>
  );
}

/**
 * Renders a base insufficient-data state inside the explanation panel.
 *
 * @param props Explanation content props.
 * @return Empty-state markup.
 */
function InsufficientDataState({ explanation, brandName }: { explanation: SentimentExplanation; brandName: string }) {
  return (
    <div className="space-y-4">
      <EmptyState
        icon={SearchX}
        title="Not enough sentiment evidence yet"
        description={`Prism could not find stored sentiment metrics for ${brandName} in ${explanation.range.label}. Run or schedule sentiment studies to build this explanation.`}
        className="border-0 py-10"
      />
      <CaveatList title="Caveats" caveats={safeArray(explanation.caveats)} />
    </div>
  );
}

/**
 * Renders the ready explanation content.
 *
 * @param props Explanation content props.
 * @return Explanation content markup.
 */
function ExplanationContent({ explanation, selectedCompetitorId, competitorOptions, onCompetitorChange }: ExplanationContentProps) {
  return (
    <div className="space-y-6">
      <ScoreSummary explanation={explanation} />
      <EvidenceSection explanation={explanation} />
      <Contributors explanation={explanation} />
      <FactGrid facts={safeArray(explanation.facts)} />
      <CaveatList title="Explanation caveats" caveats={safeArray(explanation.caveats)} />
      <CompetitorSection
        selectedCompetitorId={selectedCompetitorId}
        competitorOptions={competitorOptions}
        comparison={explanation.competitor_comparison ?? null}
        onCompetitorChange={onCompetitorChange}
      />
    </div>
  );
}

/**
 * Renders the dashboard sentiment explanation panel and owns its request state.
 *
 * @param props Sentiment explanation panel props.
 * @return Sentiment explanation panel markup.
 */
export function SentimentExplanationPanel({ brandId, brandName, rangeKey, competitorOptions, promptIds }: SentimentExplanationPanelProps) {
  const [selectedCompetitorId, setSelectedCompetitorId] = useState<number | null>(null);
  const [explanation, setExplanation] = useState<SentimentExplanation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryNonce, setRetryNonce] = useState(0);
  const validCompetitorId = getValidCompetitorId(selectedCompetitorId, competitorOptions, brandId);

  useEffect(
    /**
     * Clears stale competitor selections when the dashboard hero brand changes.
     *
     * @return void
     */
    function syncSelectedCompetitor(): void {
      if (selectedCompetitorId !== validCompetitorId) setSelectedCompetitorId(validCompetitorId);
    },
    [selectedCompetitorId, validCompetitorId]
  );

  useEffect(
    /**
     * Loads the stored-response sentiment explanation for the current dashboard context.
     *
     * @return Cleanup function for stale requests.
     */
    function loadSentimentExplanation(): () => void {
      let cancelled = false;

      /**
       * Runs the explanation request and updates local async state.
       *
       * @return Promise that resolves after state is updated.
       */
      async function fetchExplanation(): Promise<void> {
        setLoading(true);
        setError("");

        try {
          const nextExplanation = await api.metrics.sentimentExplanation({
            brandId,
            range: rangeKey,
            competitorBrandId: validCompetitorId ?? undefined,
            promptIds: promptIds && promptIds.length > 0 ? promptIds : undefined,
          });
          if (!cancelled) setExplanation(nextExplanation);
        } catch (err: unknown) {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : "Explanation unavailable");
            setExplanation(null);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      }

      void fetchExplanation();

      /**
       * Marks the in-flight explanation request as stale.
       *
       * @return void
       */
      return function cancelExplanationRequest(): void {
        cancelled = true;
      };
    },
    // promptIds may be a fresh array each render — key the effect on its content, not identity.
    [brandId, rangeKey, validCompetitorId, retryNonce, promptIds?.join(",")]
  );

  /**
   * Updates the optional competitor comparison selection.
   *
   * @param event Select change event.
   * @return void
   */
  function handleCompetitorChange(event: ChangeEvent<HTMLSelectElement>): void {
    const nextValue = event.target.value;
    setSelectedCompetitorId(nextValue ? Number(nextValue) : null);
  }

  /**
   * Retries the explanation request.
   *
   * @return void
   */
  function handleRetry(): void {
    setRetryNonce(retryNonce + 1);
  }

  return (
    <Card className="animate-fade-up" style={{ animationDelay: "40ms" }}>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-brand-600">Stored-response explanation</p>
            <CardTitle className="mt-1">Why this sentiment score?</CardTitle>
            <CardDescription className="max-w-3xl">
              Prism traces this score to stored LLM responses and sentiment metrics. No new model call is made.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-500">
            <Database className="h-3.5 w-3.5" />
            Stored data only
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Spinner message="Building explanation from stored responses…" size="lg" />
        ) : error ? (
          <ErrorState title="Explanation unavailable" message={error} onRetry={handleRetry} className="border-0 py-10" />
        ) : explanation?.state === "insufficient_data" ? (
          <InsufficientDataState explanation={explanation} brandName={brandName} />
        ) : explanation ? (
          <ExplanationContent
            explanation={explanation}
            selectedCompetitorId={validCompetitorId}
            competitorOptions={competitorOptions}
            onCompetitorChange={handleCompetitorChange}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}
